/**
 * 挂在路由定义上的自定义元数据。
 *
 * 写在路由对象上（`handle: {...}`），不要放进 `lazy()` 的返回值——React Router
 * 会先发布 pending 状态再 await lazy，在途导航期间读不到 lazy 补上的 handle。
 */
export interface IRouteHandle {
  /**
   * 跳转前后本路由都仍在匹配链上（即在它的子树内部切页）时，不显示全局 loading。
   * 挂在布局路由上即可覆盖它的全部子路由。
   */
  quietNavigation?: boolean;
  /**
   * 本路由自己处理登出，`ProtectLayout` 不再把它跳去登录页。
   *
   * 只有需要在离开前先征询用户的页面才该声明它——编辑器用它换成一个确认框，
   * 避免画布上未保存的改动被直接拽走。声明了就必须自己挂 `useAuthSync`，
   * 否则这条分支上的登出会无人处理。
   */
  ownLogout?: boolean;
}

/** 判定所需的最小匹配项形状，`useMatches()` 与 `unstable_useRouterState()` 的 match 都满足 */
interface IRouteMatchMeta {
  id: string;
  handle: unknown;
}

/**
 * 路由 handle 的类型是 any / unknown，属于边界数据，用类型守卫收窄而不是断言。
 * 只认 `true`，避免把任意真值当成开关。
 */
function isQuietHandle(handle: unknown): boolean {
  return (
    typeof handle === 'object' && handle !== null && 'quietNavigation' in handle && handle.quietNavigation === true
  );
}

/** 同 isQuietHandle，见其注释 */
function isOwnLogoutHandle(handle: unknown): boolean {
  return typeof handle === 'object' && handle !== null && 'ownLogout' in handle && handle.ownLogout === true;
}

/** 匹配链中声明了静默跳转的路由 id */
export function quietRouteIds(matches: readonly IRouteMatchMeta[]) {
  return matches.filter((match) => isQuietHandle(match.handle)).map((match) => match.id);
}

/**
 * 当前匹配链上是否有路由声明了自己处理登出。
 *
 * 不比对 id：登出只关心「此刻这条链上有没有人接管」，链上任一层声明即生效，
 * 因此挂在布局路由上同样能覆盖它的全部子路由。
 */
export function handlesOwnLogout(matches: readonly IRouteMatchMeta[]) {
  return matches.some((match) => isOwnLogoutHandle(match.handle));
}

/**
 * 跳转前后是否落在同一个声明了静默的路由子树内。
 *
 * 用 id 求交集而不是比 pathname 前缀：静默组因此不必是 URL 子树，
 * 语义也正好是「同一个布局路由在跳转两侧都还在」。
 */
export function isQuietNavigation(active: readonly IRouteMatchMeta[], pending: readonly IRouteMatchMeta[]) {
  const activeIds = quietRouteIds(active);
  return quietRouteIds(pending).some((id) => activeIds.includes(id));
}
